// src/components/institution/sections/PostsSection.jsx
import React, { useState } from 'react';
import { 
  Plus, 
  Filter, 
  Search, 
  Users, 
  Globe, 
  Lock, 
  Calendar,
  MessageCircle,
  TrendingUp
} from 'lucide-react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { PostCard } from '../posts/PostCard';
// import { PostCreationForm } from '../forms/PostCreationForm';
import { PostComposer } from '../../manager/shared/PostComposer';

export const PostsSection = ({ 
  data, 
  bookId,
  settings, 
  styling, 
  posts = [], 
  user,
  isModerator = false,
  onCreatePost,
  onUpdatePost,
  onDeletePost,
  onModeratePost,
  ...commonProps
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter posts logic (same as before)
  const getFilteredPosts = () => {
    let filteredPosts = posts;

    // Apply filters...
    if (!user) {
      filteredPosts = filteredPosts.filter(post => post.visibility === 'public' && post.status === 'approved');
    } else if (filterVisibility !== 'all') {
      filteredPosts = filteredPosts.filter(post => post.visibility === filterVisibility);
    }

    if (filterStatus !== 'all') {
      if (isModerator) {
        filteredPosts = filteredPosts.filter(post => post.status === filterStatus);
      } else if (user) {
        filteredPosts = filteredPosts.filter(post => 
          post.status === 'approved' || 
          (post.authorId === user.uid && post.status === filterStatus)
        );
      }
    } else {
      if (!isModerator && user) {
        filteredPosts = filteredPosts.filter(post => 
          post.status === 'approved' || post.authorId === user.uid
        );
      } else if (!isModerator && !user) {
        filteredPosts = filteredPosts.filter(post => post.status === 'approved');
      }
    }

    if (searchQuery) {
      filteredPosts = filteredPosts.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    return filteredPosts.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB - dateA;
    });
  };

  const filteredPosts = getFilteredPosts();

  // Post handlers (same as before)
  const handleCreatePost = async (postData) => {
    try {
      await onCreatePost(postData);
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setShowCreateForm(true);
  };

  const handleUpdatePost = async (postData) => {
    try {
      await onUpdatePost(editingPost.id, postData);
      setEditingPost(null);
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error updating post:', error);
    }
  };

  // Interaction handlers (same as before)
  const handleLike = async (postId) => {
    console.log('Like post:', postId);
  };

  const handleComment = (postId) => {
    console.log('Comment on post:', postId);
  };

  const handleShare = (post) => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.content.substring(0, 100) + '...',
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const getPostStats = () => {
    const totalPosts = posts.length;
    const pendingPosts = posts.filter(p => p.status === 'pending').length;
    const publicPosts = posts.filter(p => p.visibility === 'public' && p.status === 'approved').length;
    const internalPosts = posts.filter(p => p.visibility === 'internal' && p.status === 'approved').length;
    return { totalPosts, pendingPosts, publicPosts, internalPosts };
  };

  const stats = getPostStats();

  return (
    <section className="py-12 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 
            className="text-2xl md:text-3xl font-bold mb-4"
            style={{ color: styling?.primaryColor || '#1e40af' }}
          >
            Updates Posts
          </h2>
          <div className="w-24 h-1 mx-auto mb-8" style={{ backgroundColor: styling?.primaryColor }} />
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Stay connected with the latest news, announcements, and updates from our community.
          </p>
        </div>

        {/* Controls Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
          {/* Stats Overview */}
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{stats.totalPosts}</div>
              <div className="text-sm text-gray-600">Total Posts</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.publicPosts}</div>
              <div className="text-sm text-gray-600">Public</div>
            </div>
            {user && (
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{stats.internalPosts}</div>
                <div className="text-sm text-gray-600">Internal</div>
              </div>
            )}
            {isModerator && stats.pendingPosts > 0 && (
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{stats.pendingPosts}</div>
                <div className="text-sm text-gray-600">Pending</div>
              </div>
            )}
          </div>

          {/* Action Button */}
          {user && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Plus className="h-5 w-5" />
              Create Post
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row items-center gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex gap-2">
            <select
              value={filterVisibility}
              onChange={(e) => setFilterVisibility(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Posts</option>
              <option value="public">Public Only</option>
              {user && <option value="internal">Internal Only</option>}
            </select>

            {(isModerator || user) && (
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            )}
          </div>
        </div>

        {/* Posts Feed - Masonry Grid Layout */}
        {filteredPosts.length > 0 ? (
          <ResponsiveMasonry
            columnsCountBreakPoints={{
              350: 1,
              750: 2,
              900: 3,
              1200: 3
            }}
          >
            <Masonry gutter="1rem">
              {filteredPosts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  user={user}
                  isModerator={isModerator}
                  onEdit={handleEditPost}
                  onDelete={onDeletePost}
                  onModerate={onModeratePost}
                  onLike={handleLike}
                  onComment={handleComment}
                  onShare={handleShare}
                />
              ))}
            </Masonry>
          </ResponsiveMasonry>
        ) : (
          <div className="text-center py-12">
            <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery ? 'No posts found' : 'No posts yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchQuery 
                ? 'Try adjusting your search terms or filters'
                : user 
                  ? 'Be the first to share an update with the community!'
                  : 'Check back later for community updates and announcements.'
              }
            </p>
            {user && !searchQuery && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create First Post
              </button>
            )}
          </div>
        )}

        {/* Post Creation Form Modal */}
        {showCreateForm && (
          <PostComposer
            bookId={bookId}
            isOpen={showCreateForm}
            onClose={() => {
              setShowCreateForm(false);
              setEditingPost(null);
            }}
            onSave={editingPost ? handleUpdatePost : handleCreatePost}
            initialData={editingPost}
            user={user}
          />
        )}
      </div>
    </section>
  );
};